import { FullWidthLayout } from '@/layouts'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import * as Styled from './legalPolicies.stlye'
import { Typography, useTheme } from '@cellectenergy/cellect-react-components'
import { styled } from '@mui/material/styles'

const CustomTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.corporateColors.primary.white,
}))

const PolicyLink = styled('a')(({ theme }) => ({
  color: theme.palette.common.corporateColors.primary.white,
  display: 'inline',
  font: 'inherit',
  textDecoration: 'underline',
  textUnderlineOffset: '0.125rem',
}))

const tableCellSx = {
  borderColor: 'rgba(255, 255, 255, 0.24)',
  verticalAlign: 'top',
}

const privacyRows = [
  {
    id: 'browsing-data',
    dataProcessed: (
      <>
        <span className="body-medium weight-medium">Browsing data.</span> Details of your visits to the Website and
        information collected through cookies and other tracking technologies including, but not limited to, your IP
        address and domain name, your browser version and operating system, browser language, access time, traffic data,
        location data, web logs, movements on the Websites, referring web site addresses and other communication data.
        We may also collect information about the pages you view within the Websites and other actions you take while
        visiting us. In addition, we may also use such technologies to determine whether you&apos;ve opened an e-mail or
        clicked on a link contained in an e-mail.
        <br />
        <br />
        This information is not shared with third parties without your consent.
      </>
    ),
    legalBasis: 'Consent of the data subject or legitimate interest.',
    purposes: (
      <>
        <span className="body-medium weight-medium">Analysis of browsing behavior and statistics:</span> The information
        collected through cookies and other similar tracking technologies allow an analysis of the navigation made by
        Users.
      </>
    ),
    conservationPeriod: (
      <>
        The retention periods depend on each specific cookie. For more information on the information retention periods
        for each type of cookie, please consult the <PolicyLink href="/cookies">Cookies Policy</PolicyLink>.
      </>
    ),
  },
  {
    id: 'contact-information',
    dataProcessed: (
      <>
        <span className="body-medium weight-medium">Contact information.</span> First name, last name, e-mail.
      </>
    ),
    legalBasis: 'Consent of the data subject.',
    purposes:
      'To attend and respond to requests, comments, suggestions. As well as providing support to the User during the use of the Website.',
    conservationPeriod:
      "The personal data will be processed by Cellect for the period necessary to answer the question and/or resolve the incident indicated by the User. Said data will be blocked in order to comply with Cellect's legal obligations and, once this period has elapsed, will be definitively deleted.",
  },
  {
    id: 'commercial-communications',
    dataProcessed: (
      <>
        <span className="body-medium weight-medium">Commercial communications.</span> The User, if he/she so wishes, may
        subscribe to Cellect&apos;s Newsletter, complete forms to receive our Whitepapers or receive commercial
        communications regarding Cellect&apos;s products. For these purposes, the following data will be processed: first
        name, last name, email address, and company and industry details.
      </>
    ),
    legalBasis: 'Consent of the data subject.',
    purposes: (
      <>
        If you have consented to receive commercial communications, this data is used to send you{' '}
        <span className="body-medium weight-medium">commercial information about Cellect</span>. For more information on
        commercial communications, please see our <PolicyLink href="/legal">Legal Notice</PolicyLink>.
      </>
    ),
    conservationPeriod:
      'The data will be processed by Cellect until the User withdraws his/her consent. Once consent has been withdrawn, Cellect will block your data for the time necessary to comply with legal obligations. At the end of this period, the data will be definitively deleted.',
  },
  {
    id: 'book-a-demo',
    dataProcessed: (
      <>
        <span className="body-medium weight-medium">Book a demo.</span> You can book a meeting with one of our sales
        representatives to arrange a demo. For this purpose, your first name, last name, and email address will be
        processed.
      </>
    ),
    legalBasis: 'Consent of the data subject.',
    purposes:
      "Your personal data will be processed to schedule a meeting with our sales team to provide a demo of Cellect's products and address your questions.",
    conservationPeriod:
      'The data will be processed by Cellect until the User withdraws his/her consent. Once consent has been withdrawn, Cellect will block your data for the time necessary to comply with legal obligations. At the end of this period, the data will be definitively deleted.',
  },
]

export const PrivacyScreen = () => {
  const theme = useTheme()
  return (
    <FullWidthLayout id="free-trial" bgColor={theme.palette.common.corporateColors.primary.midNightBlue}>
      <Styled.LegalContainer>
        <Styled.BoxContent>
          <CustomTypography variant="headline-medium" fontWeight={500}>
            Privacy Policy
          </CustomTypography>
        </Styled.BoxContent>

        <Styled.BoxContent>
          <CustomTypography variant="title-large" fontWeight={500}>
            Who we are
          </CustomTypography>
          <CustomTypography variant="body-medium">
            CELLECT ENERGY S.L. (hereinafter &quot;Cellect&quot;) is the owner of the Web Site{' '}
            <PolicyLink href="https://cellectenergy.com/">https://cellectenergy.com/</PolicyLink> (hereinafter the
            &quot;Web Site&quot;) and is responsible for the processing of the personal data of its users (hereinafter
            the &quot;Users&quot;).
          </CustomTypography>
          <CustomTypography variant="body-medium">
            Through this Privacy Policy, and in compliance with Articles 12 and 13 of Regulation (EU) 2016/679
            (hereinafter &quot;GDPR&quot;) and Article 11 of Organic Law 3/2018, of December 5, on the Protection of
            Personal Data and guarantee of digital rights (&quot;LOPDPGDD&quot;), Cellect informs Users using the
            Website about the processing of their personal data that may be collected through this and processed by
            Cellect.
          </CustomTypography>
        </Styled.BoxContent>

        <Styled.BoxContent>
          <CustomTypography variant="title-large" fontWeight={500}>
            Information about the data controller
          </CustomTypography>
          <CustomTypography variant="body-medium">
            <span className="body-medium weight-medium">Data Controller:</span> CELLECT ENERGY S.L
          </CustomTypography>
          <CustomTypography variant="body-medium">
            <span className="body-medium weight-medium">TAX ID:</span> B67724062
          </CustomTypography>
          <CustomTypography variant="body-medium">
            <span className="body-medium weight-medium">Registered office:</span> Josep Tarradellas, 8-10, 4-4, 08029
            Barcelona, Spain
          </CustomTypography>
          <CustomTypography variant="body-medium">
            <span className="body-medium weight-medium">Contact us:</span>{' '}
            <PolicyLink href="mailto:privacy@cellectenergy.com">privacy@cellectenergy.com</PolicyLink> or +34 931 260
            211.
          </CustomTypography>
        </Styled.BoxContent>

        <Styled.BoxContent>
          <CustomTypography variant="title-large" fontWeight={500}>
            Data processed, legal basis, purposes and retention period
          </CustomTypography>
          <div style={{ width: '100%', overflow: 'auto' }}>
            <TableContainer sx={{ maxWidth: '100%' }}>
              <Table sx={{ minWidth: 900 }} aria-label="privacy data table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableCellSx}>
                      <CustomTypography variant="body-large" fontWeight={500}>
                        Data processed
                      </CustomTypography>
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      <CustomTypography variant="body-large" fontWeight={500}>
                        Legal Basis
                      </CustomTypography>
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      <CustomTypography variant="body-large" fontWeight={500}>
                        Purposes
                      </CustomTypography>
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      <CustomTypography variant="body-large" fontWeight={500}>
                        Conservation period
                      </CustomTypography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {privacyRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell sx={tableCellSx}>
                        <CustomTypography variant="body-medium">{row.dataProcessed}</CustomTypography>
                      </TableCell>
                      <TableCell sx={tableCellSx}>
                        <CustomTypography variant="body-medium">{row.legalBasis}</CustomTypography>
                      </TableCell>
                      <TableCell sx={tableCellSx}>
                        <CustomTypography variant="body-medium">{row.purposes}</CustomTypography>
                      </TableCell>
                      <TableCell sx={tableCellSx}>
                        <CustomTypography variant="body-medium">{row.conservationPeriod}</CustomTypography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </Styled.BoxContent>

        <Styled.BoxContent>
          <CustomTypography variant="title-large" fontWeight={500}>
            To which recipients will your data be communicated?
          </CustomTypography>
          <CustomTypography variant="body-medium">
            Cellect informs that, for the fulfillment of the purposes described above, it will not transfer the
            User&apos;s personal data to third parties. Notwithstanding the foregoing, Cellect may give access to such
            personal data to:
          </CustomTypography>
          <ul>
            <li>
              <CustomTypography variant="body-medium">
                the Sales, Communication, or Information technology teams of Cellect.
              </CustomTypography>
            </li>
            <li>
              <CustomTypography variant="body-medium">
                technology service providers with which it has entered into a processor contract; in particular to
                hosting providers, backend infrastructure providers, infrastructure monitoring providers, managing
                contacts and sending messages service providers, support and contact request management service
                providers, user database management service provider, database maintenance and newsletter subscription
                management service providers, analytical service providers, consent Management Platform Providers
                (Consent Management Platform).
              </CustomTypography>
            </li>
          </ul>
          <CustomTypography variant="body-medium">
            The mentioned providers may be located in jurisdictions that generally do not offer adequate safeguards
            regarding the processing of personal data. For all entities outside the European Economic Area (EEA),
            Cellect has entered into agreements with such entities that include these safeguards, including the European
            Commission&apos;s Standard Contractual Clauses.
          </CustomTypography>
          <CustomTypography variant="body-medium">
            Users may obtain more information about technology service providers by requesting the same from Cellect
            through the means of contact provided in this privacy policy.
          </CustomTypography>
          <CustomTypography variant="body-medium">Also, Cellect may communicate your data to:</CustomTypography>
          <ul>
            <li>
              <CustomTypography variant="body-medium">
                Companies interested in buying or acquiring the company or a part of its business and, consequently,
                give access to any national or international auditor to carry out their due diligence.
              </CustomTypography>
            </li>
            <li>
              <CustomTypography variant="body-medium">
                Authorities to investigate suspected fraud, harassment or other violations of any law, rule or
                regulation, or of the website policies.
              </CustomTypography>
            </li>
          </ul>
        </Styled.BoxContent>

        <Styled.BoxContent>
          <CustomTypography variant="title-large" fontWeight={500}>
            Security and confidentiality
          </CustomTypography>
          <CustomTypography variant="body-medium">
            Cellect undertakes to adopt the necessary technical and organizational measures, in accordance with the
            provisions of the applicable regulations, to ensure the security of personal data and prevent the accidental
            or unlawful destruction, loss or alteration of personal data transmitted, stored or otherwise processed, or
            unauthorized communication or access to such data. Personal data will be treated confidentially by Cellect,
            which undertakes to inform and enforce, by legal or contractual obligation, such confidentiality to its
            employees, partners and any person who should have access to the User&apos;s personal data.
          </CustomTypography>
        </Styled.BoxContent>

        <Styled.BoxContent>
          <CustomTypography variant="title-large" fontWeight={500}>
            Users&apos; rights
          </CustomTypography>
          <CustomTypography variant="body-medium">
            The User may exercise the rights of access, rectification, deletion, opposition and, where appropriate,
            limitation of processing and portability of data. Users may revoke the consent given at any time.
          </CustomTypography>
          <CustomTypography variant="body-medium">
            The aforementioned rights may be exercised by sending an email to{' '}
            <PolicyLink href="mailto:privacy@cellectenergy.com">privacy@cellectenergy.com</PolicyLink> specifying the
            following details: name and surname(s), address for the purpose of notifications, and/or your user account.
          </CustomTypography>
          <CustomTypography variant="body-medium">
            Likewise, the User may file a complaint with a supervisory authority and, in particular, with the Spanish
            Data Protection Agency (<PolicyLink href="https://www.aepd.es/">www.aepd.es</PolicyLink>) if he/she
            considers that the rights set forth above in this Privacy Policy have been violated or if he/she considers
            that the processing of personal data concerning him/her violates the applicable regulations.
          </CustomTypography>
        </Styled.BoxContent>

        <Styled.BoxContent>
          <CustomTypography variant="title-large" fontWeight={500}>
            Modifications
          </CustomTypography>
          <CustomTypography variant="body-medium">
            Cellect reserves the right to modify this Privacy Policy at any time. Changes or updates to the Privacy
            Policy will be explicitly notified to the User by means of a notice on the Website, together with the
            updated version of the Privacy Policy.
          </CustomTypography>
          <CustomTypography variant="body-medium">This Privacy Policy was last updated in April 2026.</CustomTypography>
        </Styled.BoxContent>
      </Styled.LegalContainer>
    </FullWidthLayout>
  )
}
